import { Navigate } from 'react-router-dom';
import { useStoreDetail } from '@/hooks/useAppData';
import { useEntityPage } from '@/hooks/useEntityPage';
import { useTrackView } from '@/hooks/useTrackView';
import { PageHead } from '@/components/seo/PageHead';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { AboutSection } from '@/components/detail/AboutSection';
import { EntityDetailLayout } from '@/components/detail/EntityDetailLayout';
import { OpeningHoursCard } from '@/components/detail/OpeningHoursCard';
import { CategoryChips } from '@/components/category/CategoryChips';
import { Link } from 'react-router-dom';
import { LocationMapCard } from '@/components/map/LocationMapCard';
import { BrandCard, PromotionCard } from '@/components/cards';
import { CityStoreList } from '@/components/detail/StoreList';
import { Chip, SectionHeader } from '@/components/ui';
import { safeImageUrl, ImagePresets } from '@/utils/safeImageUrl';
import { cityLabelAmong, otherStoresInCity, storeAddressLine } from '@/seo/brandStores';
import { storePageHead } from '@/seo/pageHeads';
import { detailSeoOptions } from '@/utils/detailStatus';
import { cleanName } from '@/seo/text';
import { entityPath } from '@/seo/urls';

/**
 * Store detail — /store/:ref and /pk/en/store/:ref, an id or a slug (web plan
 * W0.16, `useEntityPage`). Views, favorites and follows use the store's id.
 *
 * Like the shop page (address line + location map in the aside) plus, from
 * its brand (web plan W0.10): "Offers from {Brand}" (the offers that name
 * this store first, then the rest of the brand's), "Other {Brand} stores in
 * {City}" and a "Brand" section linking back to the brand. The address line
 * under the H1 ends with the city. EntityDetailLayout owns the cover, logo,
 * title row, favorite/follow buttons, stats, contact (the phone numbers) and
 * socials. Backend B3.4: "About {Store}" from its description and its opening
 * hours (aside, above the map), each only when entered; a staff
 * `seoOverrides` title/description reaches the head through `storePageHead`.
 */
export function StoreDetailPage() {
  const { item: store, status, errorCode, refetch, redirect } = useEntityPage(useStoreDetail);
  useTrackView('store', store?.id, status);

  // Title, description, H1 and breadcrumbs from the store formula (web plan
  // W0.6 / W3.8): "{Brand} – {Area}, {City}" with B3.2's `area` field. The
  // store's own name ("Batik - Packages Mall Lahore") is shown under the H1
  // when the formula leaves it out. JSON-LD (W0.7): a Store with its
  // PostalAddress, part of its brand. The SEO server builds the same head from
  // the same query (web plan W1.3).
  const t = useTranslate();
  const locale = useRouteLocale();
  const head = store && status === 'ok' ? storePageHead(store, { t, locale }) : null;
  const storeName = store ? cleanName(store.name) || store.name : '';
  const pageHead = (
    <PageHead
      // `noindex, follow` in every state (master D-5, web plan W0.3): store pages
      // stay out of the index until the backend's store clean-up (B3.2) vouches
      // for each one, but their links are still followed and brand pages keep
      // linking here. (storeHead's robots say the same.) The one exception is a
      // loaded store whose backend `seo.robots` says otherwise (web plan W0.16):
      // the backend owns that decision, and storeHead emits its value verbatim.
      noindex={!(status === 'ok' && store?.seo?.robots?.trim())}
      head={head}
      // Loading / not found / failed load (web plan W0.2): "Store · PromoStork"
      // or "Store not found", never the homepage title. Their `keepRobots`
      // never beats the `noindex` above.
      {...(status === 'ok' ? {} : detailSeoOptions('store', status))}
    />
  );

  const categories = store?.categories ?? [];
  const tags = store?.tags ?? [];
  const brand = store?.brand ?? null;
  const brandName = brand ? cleanName(brand.name) || brand.name : '';
  // W3.8: the backend's `activePromotions` field (B3.7a) already returns the
  // store's page promotions in display order (running first, then upcoming,
  // store's own first), replacing the old client-side storeOffers merge.
  const promotions = store?.activePromotions ?? [];
  const brandStores = brand?.brandStores ?? [];
  // The brand page's label for this city ("DHA Lahore", even when this store
  // typed "dha lahore"), for the section heading and every row's chip.
  const cityName = cityLabelAmong(brandStores, store?.city);
  const nearby = store ? otherStoresInCity(brandStores, store) : [];
  // "See all N {Brand} stores" only when some are in other cities.
  const storesElsewhere = brandStores.filter((s) => s.id !== store?.id).length > nearby.length;
  const addressLine = store ? storeAddressLine(store) : '';

  if (redirect) return <Navigate replace to={redirect.to} state={redirect.state} />;

  return (
    <EntityDetailLayout
      kind="store"
      entity={store}
      pageHead={pageHead}
      status={status}
      errorCode={errorCode}
      onRetry={refetch}
      h1={head?.h1}
      // The visible trail is the BreadcrumbList's own array (web plan W0.8):
      // Home › Brands › {Brand} › {H1}, no city crumb before W3.3.
      breadcrumbs={head?.breadcrumbs}
      subtitle={
        head && !head.h1.toLowerCase().includes(storeName.toLowerCase()) ? storeName : null
      }
      addressLine={addressLine || null}
      // The pill counts the offers listed below, the brand's included.
      promotionCount={promotions.length}
      asideExtra={
        store ? (
          <>
            {/* After the layout's Contact card (the phone numbers) and links,
                above the map; only when entered (backend B3.4). */}
            <OpeningHoursCard openingHours={store.openingHours} />
            <LocationMapCard
              location={store.location}
              googleMapsPlaceID={store.googleMapsPlaceID}
              address={addressLine || null}
              name={store.name}
              kind="store"
              logoUrl={safeImageUrl(store.logo, ImagePresets.logo)}
              entityId={store.id}
            />
          </>
        ) : null
      }
    >
      {store && (
        <div className="space-y-8">
          {categories.length > 0 && (
            <section aria-label="Categories">
              {/* A store is one of its brand's outlets, and search has no
                  stores segment: its categories search brands (was "shops"). */}
              <CategoryChips categories={categories} segment="brands" />
            </section>
          )}

          {promotions.length > 0 && (
            <section aria-label={brand ? `Offers from ${brandName}` : 'Offers'}>
              <SectionHeader title={brand ? `Offers from ${brandName}` : 'Offers'} wrap />
              <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
                {promotions.map((promotion) => (
                  <PromotionCard key={promotion.id} promotion={promotion} />
                ))}
              </div>
            </section>
          )}

          {brand && cityName && nearby.length > 0 && (
            <section aria-label={`Other ${brandName} stores in ${cityName}`}>
              <SectionHeader title={`Other ${brandName} stores in ${cityName}`} wrap />
              <CityStoreList stores={nearby} fallbackLogo={brand.logo} city={cityName} />
              {storesElsewhere && (
                <Link
                  to={entityPath(brand, 'brand')}
                  className="mt-3 inline-block text-sm font-bold text-primary-strong hover:underline"
                >
                  See all {brandStores.length} {brandName} stores
                </Link>
              )}
            </section>
          )}

          {/* The store's own staff-written description (backend B3.4), when any. */}
          <AboutSection name={storeName} description={store.description} />

          {brand && (
            <section aria-label="Brand">
              <SectionHeader title="Brand" />
              <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
                <BrandCard brand={brand} />
              </div>
            </section>
          )}

          {tags.length > 0 && (
            <section aria-label="Tags">
              <SectionHeader title="Tags" />
              <div className="mt-4 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Chip key={tag.id} label={tag.title} color="neutral" outline />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </EntityDetailLayout>
  );
}

export default StoreDetailPage;
